import {
    DepositBonusEligibilityResponse,
    DepositBonusEligibilityStep,
    DepositBonusPromotion,
    DepositBonusPromotionStep,
    DepositBonusStepStatus,
    VendorBonusClaim
} from '@/types/pos/depositBonus'

export const isClaimedBonusStep = (status?: string) =>
    status === 'EARNED' || status === 'FULFILLED'

export const isNextBonusStep = (status?: string) => status === 'AVAILABLE'

export const getCampaignValidityDays = (
    promotion: DepositBonusPromotion
): number | null => {
    if (!promotion.startsAt || !promotion.endsAt) return null
    const start = Date.parse(promotion.startsAt)
    const end = Date.parse(promotion.endsAt)
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null
    return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)))
}

export const sortBonusSteps = <
    T extends { depositOrdinal: number }
>(
    steps: T[]
): T[] => [...steps].sort((a, b) => a.depositOrdinal - b.depositOrdinal)

export const toPreviewSteps = (
    steps: DepositBonusPromotionStep[]
): DepositBonusEligibilityStep[] =>
    sortBonusSteps(steps).map((step) => ({
        ...step,
        status: 'UPCOMING' as DepositBonusStepStatus
    }))

export const getPromotionTotalAmount = (
    promotion: DepositBonusPromotion
): number =>
    (promotion.steps ?? []).reduce((total, step) => {
        return total + (typeof step.amount === 'number' ? step.amount : 0)
    }, 0)

export const formatPromotionTotalAmount = (amount: number): string =>
    new Intl.NumberFormat('sr-RS', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount)

/** Picks the highest-value campaign per vendor when more than one is returned. */
export const mapPromotionsByWalletId = (
    promotions: DepositBonusPromotion[]
): Map<number, DepositBonusPromotion> => {
    const byWalletId = new Map<number, DepositBonusPromotion>()

    for (const promotion of promotions) {
        const existing = byWalletId.get(promotion.vendorWalletId)
        if (
            !existing ||
            getPromotionTotalAmount(promotion) >
                getPromotionTotalAmount(existing)
        ) {
            byWalletId.set(promotion.vendorWalletId, promotion)
        }
    }

    return byWalletId
}

export const getVendorBonusClaim = (
    eligibility: DepositBonusEligibilityResponse | null,
    vendorName: string,
    vendorLogoUrl?: string | null
): VendorBonusClaim | null => {
    if (!eligibility?.isEligible) return null

    const steps = sortBonusSteps(eligibility.steps ?? [])
    const available = steps.find((step) => isNextBonusStep(step.status))
    const rewardAmount = eligibility.reward?.amount ?? available?.amount ?? 0
    if (rewardAmount <= 0) return null

    const depositOrdinal =
        available?.depositOrdinal ?? eligibility.depositOrdinal ?? 1
    const currencyCode =
        eligibility.reward?.currencyCode ||
        available?.currencyCode ||
        'RSD'

    return {
        vendorName,
        vendorLogoUrl,
        steps:
            steps.length > 0
                ? steps
                : [
                      {
                          depositOrdinal,
                          amount: rewardAmount,
                          currencyCode,
                          voucherType:
                              eligibility.reward?.voucherType ||
                              'DEPOSIT_BONUS_VOUCHER',
                          status: 'AVAILABLE'
                      }
                  ],
        rewardAmount,
        currencyCode,
        depositOrdinal
    }
}

export const hasLaterBonusSteps = (
    steps: DepositBonusEligibilityStep[],
    currentOrdinal: number
) => steps.some((step) => step.depositOrdinal > currentOrdinal)

export const hasRemainingBonusSteps = (
    eligibility: DepositBonusEligibilityResponse
) => {
    const steps = sortBonusSteps(eligibility.steps ?? [])
    if (steps.length > 0) {
        return steps.some((step) => !isClaimedBonusStep(step.status))
    }
    const max = eligibility.maxBonusCount ?? 0
    const claimed = eligibility.claimedBonusCount ?? 0
    return max > 0 && claimed < max
}

/** Dialog: claimable today, or already used today with later days still left. */
export const shouldShowVendorBonusOverlay = (
    eligibility: DepositBonusEligibilityResponse | null
) => {
    if (!eligibility?.hasCampaign) return false
    if (!hasRemainingBonusSteps(eligibility)) return false
    return eligibility.isEligible || eligibility.alreadyUsedToday
}

/** Last step that should be “checked” — AVAILABLE reward, or the last claimed day. */
export const getBonusCheckThroughIndex = (
    steps: DepositBonusEligibilityStep[]
) => {
    const availableIndex = steps.findIndex((step) =>
        isNextBonusStep(step.status)
    )
    if (availableIndex >= 0) return availableIndex

    let lastClaimedIndex = -1
    steps.forEach((step, index) => {
        if (isClaimedBonusStep(step.status)) lastClaimedIndex = index
    })
    return lastClaimedIndex
}
